import { useEffect, useState } from "react";
import { Box, Typography, Button, Input, IconButton } from "@mui/joy";
import { Plus, Trash2 } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { TextField, Textarea } from "../../../../../shared";

export default function CategoryConfiguration() {
    const { watch, setValue } = useFormContext();
    const tagsRecord = watch("Tags") || {};

    const [tagsList, setTagsList] = useState<{ key: string; value: string }[]>(() =>
        Object.entries(tagsRecord).map(([key, value]) => ({ key, value: value as string }))
    );

    useEffect(() => {
        const newRecord: Record<string, string> = {};
        tagsList.forEach((t) => {
            if (t.key.trim()) {
                newRecord[t.key.trim()] = t.value;
            }
        });
        setValue("Tags", newRecord, { shouldDirty: true });
    }, [tagsList, setValue]);

    const handleAddTag = () => {
        setTagsList([...tagsList, { key: "", value: "" }]);
    };

    const handleUpdateTag = (index: number, field: "key" | "value", newValue: string) => {
        const newList = [...tagsList];
        newList[index][field] = newValue;
        setTagsList(newList);
    };

    const handleRemoveTag = (index: number) => {
        setTagsList(tagsList.filter((_, i) => i !== index));
    };

    return (
        <Box flex={1}>
            <Box display={"flex"} flexDirection={"column"} gap={1}>
                <TextField
                    name={"Name"}
                    label={"Category Name"}
                    helperText={"Enter the name of the product category"}
                    placeholder={"e.g., Field Shingles, Starter, Hip & Ridge"}
                />
                <Textarea
                    name={"Description"}
                    label={"Description"}
                    helperText={"Optional description of the category"}
                    placeholder={"Describe the purpose and usage of this category..."}
                    minRows={3}
                />
                <TextField
                    name={"DefaultUnitType"}
                    label={"Default Unit Type"}
                    helperText={"Optional default unit type (e.g., SQ, LF, Count)"}
                    placeholder={"e.g., SQ"}
                />

                <Box mt={2}>
                    <Typography level="title-sm">Tags</Typography>
                    <Typography level="body-xs" mb={1.5}>
                        Key-Value pairs to associate with this category
                    </Typography>

                    {tagsList.map((t, idx) => (
                        <Box key={idx} display="flex" gap={1} mb={1}>
                            <Input
                                placeholder="Key"
                                value={t.key}
                                onChange={(e) => handleUpdateTag(idx, "key", e.target.value)}
                                sx={{ flex: 1 }}
                            />
                            <Input
                                placeholder="Value"
                                value={t.value}
                                onChange={(e) => handleUpdateTag(idx, "value", e.target.value)}
                                sx={{ flex: 1 }}
                            />
                            <IconButton color="danger" variant="plain" onClick={() => handleRemoveTag(idx)}>
                                <Trash2 size={16} />
                            </IconButton>
                        </Box>
                    ))}
                    
                    <Button
                        variant="outlined"
                        color="neutral"
                        startDecorator={<Plus size={16} />}
                        onClick={handleAddTag}
                        size="sm"
                        sx={{ mt: 1, width: "fit-content" }}
                    >
                        Add Tag
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
